/**
 * localStorage access, wrapped so a failure can never break the app.
 *
 * Storage throws more often than people expect: Safari private mode, a full
 * quota, and browsers with third-party storage blocked all reject `setItem`.
 * Since this app is offline-first and its persistence is a convenience rather
 * than a requirement, every operation here fails quietly and the app carries on
 * with in-memory state.
 */

/** Namespaced so we never collide with anything else on the same origin. */
const PREFIX = 'linkedin-optimiser:';

export const STORAGE_KEYS = {
  profile: `${PREFIX}profile`,
  mode: `${PREFIX}mode`,
  theme: `${PREFIX}theme`,
} as const;

function storage(): Storage | null {
  try {
    // Touching `localStorage` itself throws when storage is blocked, so the
    // access has to be inside the try, not just the call.
    const probe = window.localStorage;
    const key = `${PREFIX}__probe`;
    probe.setItem(key, '1');
    probe.removeItem(key);
    return probe;
  } catch {
    return null;
  }
}

export function readJson<T>(key: string, fallback: T): T {
  const store = storage();
  if (!store) return fallback;
  try {
    const raw = store.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    // Corrupt or half-written JSON. Discard it rather than crash on every load.
    try {
      store.removeItem(key);
    } catch {
      /* nothing more we can do */
    }
    return fallback;
  }
}

export function writeJson(key: string, value: unknown): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    /* quota or private mode — the app works without persistence */
  }
}

export function remove(key: string): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Removes everything this app has stored. Backs the "Clear my data" button —
 * the promise that nothing is uploaded is only credible if leaving is easy.
 */
export function clearAll(): void {
  const store = storage();
  if (!store) return;
  try {
    const ours = Object.keys(store).filter((key) => key.startsWith(PREFIX));
    for (const key of ours) store.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function isStorageAvailable(): boolean {
  return storage() !== null;
}
